import {
  OptimizelyProvider as Provider,
  ReactSDKClient,
  createInstance,
  eventDispatcher,
  setLogLevel,
  setLogger,
} from '@optimizely/react-sdk';
import * as sp from '@utilitywarehouse/tracking-adapter-snowplow-web';
import { FC, ReactNode, useEffect, useState } from 'react';

import { OPTIMIZELY_SDK_KEY } from '@constants/config';
import { useAppointmentId } from '@store/appointment/selectors';
import { useBasketId } from '@store/basket/selectors';
import { isNullish } from '@utils/isNullish';

import { initNotificationListeners } from './notificationListeners';

const isLocalEnv = process.env.NODE_ENV === 'development';

const initOptimizelyClient = () => {
  let optly: ReactSDKClient;

  return () => {
    if (!optly) {
      optly = createInstance({
        sdkKey: OPTIMIZELY_SDK_KEY,
        eventDispatcher,
        eventBatchSize: 10,
        eventFlushInterval: 1000,
      });
    }

    return optly;
  };
};

const getOptimizelyClient = initOptimizelyClient();

const getLocalUser = () => {
  let optlyUserId = window.localStorage.getItem('optlyUserId');
  if (!optlyUserId) {
    optlyUserId = 'localUser';
    window.localStorage.setItem('optlyUserId', optlyUserId);
  }

  return optlyUserId;
};

const getUserId = () => {
  const domainUser = sp.client.getDomainUserId();

  if (isLocalEnv) {
    window.optlyUserId = getLocalUser();
  }

  return isLocalEnv ? window.optlyUserId : domainUser;
};

interface OptimizelyProviderProps {
  children: ReactNode;
}

// Ensures that router can be rendered with feature flags
const FeaturesReadyWrapper: FC<OptimizelyProviderProps> = ({ children }) => {
  const [isClientReady, setIsClientReady] = useState(false);
  const optimizelyClient = getOptimizelyClient();

  const timeout = setTimeout(() => {
    setIsClientReady(true);
  }, 500);

  optimizelyClient.onReady().then(() => {
    if (isNullish(optimizelyClient.getOptimizelyConfig())) {
      return;
    }

    clearTimeout(timeout);
    setIsClientReady(true);
  });

  return isClientReady ? children : null;
};

const OptimizelyProvider = ({ children }: OptimizelyProviderProps) => {
  const optimizelyClient = getOptimizelyClient();
  if (isLocalEnv) {
    setLogLevel('warning');
  } else {
    setLogger(null);
  }

  const appointmentId = useAppointmentId();
  const basketId = useBasketId();

  const userId = appointmentId && basketId ? basketId : (getUserId() as string);

  useEffect(() => {
    if (optimizelyClient) {
      initNotificationListeners(optimizelyClient);
    }
  }, [optimizelyClient]);

  if (!optimizelyClient) {
    return children;
  }

  return (
    <Provider
      optimizely={optimizelyClient}
      user={{
        id: userId,
        attributes: {},
      }}
    >
      <FeaturesReadyWrapper>{children}</FeaturesReadyWrapper>
    </Provider>
  );
};

export { OptimizelyProvider, getOptimizelyClient };
